import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Clock, CheckCircle2, XCircle, Trash2 } from "lucide-react";

const DownloadsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchDownloads();
    // Poll for updates every 5 seconds
    const interval = setInterval(fetchDownloads, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchDownloads = async () => {
    try {
      const response = await axios.get(`${API}/downloads`);
      setDownloads(response.data);
    } catch (error) {
      console.error("Failed to fetch downloads:", error);
      if (loading) {
        toast.error("Downloads konnten nicht geladen werden");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Möchten Sie diesen Download wirklich löschen?")) {
      return;
    }

    setDeletingId(jobId);
    try {
      await axios.delete(`${API}/downloads/${jobId}`);
      toast.success("Download gelöscht");
      fetchDownloads();
    } catch (error) {
      console.error("Failed to delete download:", error);
      toast.error("Download konnte nicht gelöscht werden");
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
        return (
          <Badge variant="secondary" className="gap-1.5">
            <Clock className="w-3 h-3" />
            Wartend
          </Badge>
        );
      case "PROCESSING":
        return (
          <Badge variant="secondary" className="gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" />
            Wird verarbeitet
          </Badge>
        );
      case "COMPLETED":
        return (
          <Badge variant="default" className="gap-1.5 bg-green-600 hover:bg-green-700">
            <CheckCircle2 className="w-3 h-3" />
            Abgeschlossen
          </Badge>
        );
      case "FAILED":
        return (
          <Badge variant="destructive" className="gap-1.5">
            <XCircle className="w-3 h-3" />
            Fehlgeschlagen
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getDaysUntilExpiry = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = 7 * 24 * 60 * 60 * 1000 - (now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Downloads</h1>
            <p className="text-slate-600 mt-1">
              Ihre Download-Jobs werden hier angezeigt. Dateien werden nach 7 Tagen automatisch gelöscht.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
        </div>

        {downloads.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-slate-400 mb-4">
                <Download className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Keine Downloads</h3>
              <p className="text-slate-600">
                Sie haben noch keine Batch-Downloads erstellt. Erstellen Sie einen Download auf der Artikel-Seite.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {downloads.map((download) => (
              <Card key={download.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{download.filename}</CardTitle>
                      <CardDescription>
                        Erstellt am {formatDate(download.created_at)}
                        {download.status === "COMPLETED" && (
                          <span className="ml-2 text-xs">
                            (verfügbar noch {getDaysUntilExpiry(download.created_at)} Tage)
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    <div className="ml-4">{getStatusBadge(download.status)}</div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-4 text-sm text-slate-600">
                      {download.status === "COMPLETED" && (
                        <>
                          <span>Größe: {formatFileSize(download.file_size)}</span>
                        </>
                      )}
                      {download.status === "FAILED" && download.error_message && (
                        <span className="text-red-600">Fehler: {download.error_message}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {download.status === "COMPLETED" && download.hetzner_url && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => window.open(download.hetzner_url, "_blank")}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Herunterladen
                        </Button>
                      )}
                      {(user?.role === "admin" || download.created_by === user?.email) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(download.id)}
                          disabled={deletingId === download.id}
                        >
                          {deletingId === download.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadsPage;
